import React from "react";
import { ShieldCheck, GraduationCap, HeartHandshake, Users } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { useLang } from "../contexts/LanguageContext";
import { t } from "../i18n/translations";
import useSEO from "../hooks/useSEO";

const PageHeader = ({ eyebrow, title, intro, heroImage }) => (
  <section className="relative bg-[#0A192F] border-b border-slate-200">
    {heroImage && (
      <>
        <img src={heroImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F]/85 via-[#0A192F]/60 to-[#0A192F]/30" />
      </>
    )}
    <div className={`relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 ${heroImage ? "pt-36 pb-24 lg:pt-44 lg:pb-32" : "pt-24 pb-14 lg:pt-32 lg:pb-20"}`}>
      <Reveal>
        <p className={`text-xs uppercase tracking-[0.28em] font-bold ${heroImage ? "text-[#F2B53A]" : "text-[#E9A229]"}`}>{eyebrow}</p>
        <h1 className={`mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] max-w-3xl ${heroImage ? "text-white" : "text-[#0A192F]"}`}>{title}</h1>
        <div className="mt-6 h-1 w-16 bg-[#E9A229]" />
        {intro && <p className={`mt-6 text-lg leading-relaxed max-w-3xl ${heroImage ? "text-slate-100/90" : "text-slate-700"}`}>{intro}</p>}
      </Reveal>
    </div>
  </section>
);

const valueIcons = [ShieldCheck, GraduationCap, HeartHandshake, Users];

const About = () => {
  const { lang } = useLang();
  useSEO({
    title: lang === "en"
      ? "About Revelo — Our Story & Mission"
      : "關於 Revelo — 我們的故事與使命",
    description: lang === "en"
      ? "Learn about Revelo's mission, values and the youth team building Hong Kong's anti-scam education movement."
      : "了解 Revelo 的使命、核心價值，以及推動香港反詐騙教育的青年團隊。",
    lang,
  });
  const tr = t[lang];
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow={lang === "en" ? "Our story" : "我們的故事"}
        title={tr.about.title}
        intro={tr.about.body}
        heroImage="/about-hero.JPG"
      />

      <section className="py-16 lg:py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E9A229] font-semibold">{lang === "en" ? "The problem" : "問題所在"}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-tight">{tr.why.title}</h2>
            {tr.why.intro && <p className="mt-6 text-slate-600 text-lg leading-relaxed">{tr.why.intro}</p>}
          </Reveal>
          <StaggerGroup className="mt-12 grid md:grid-cols-3 gap-6">
            {tr.why.cards.map((c, i) => (
              <StaggerItem key={i}>
                <div data-testid={`about-why-${i}`} className="h-full bg-white border border-slate-100 rounded-none p-7 hover:shadow-md transition-all">
                  <span className="inline-block text-xs font-mono text-[#E9A229] font-bold">0{i + 1}</span>
                  <h3 className="mt-3 font-heading font-semibold text-xl text-[#0A192F]">{c.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{c.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#E9A229] font-semibold">{lang === "en" ? "Our work" : "我們的工作"}</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A192F] leading-tight">{tr.what.title}</h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">{tr.what.intro}</p>
          </Reveal>
          <StaggerGroup className="mt-12 grid md:grid-cols-2 gap-6">
            {tr.what.features.map((f, i) => (
              <StaggerItem key={i}>
                <div data-testid={`about-what-${i}`} className="h-full bg-white border border-slate-100 rounded-none p-8 hover:shadow-md transition-all flex gap-5">
                  <div className="shrink-0 h-12 w-12 rounded-none bg-gradient-to-br from-[#163B71] to-[#25539A] text-white flex items-center justify-center font-bold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg text-[#0A192F]">{f.title}</h3>
                    <p className="mt-2 text-slate-600 leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>
    </div>
  );
};

export { PageHeader };
export default About;
